"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="7656" className="flex gap-10">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted animate-pulse rounded-sm" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-[10px] font-medium uppercase tracking-[0.1em] text-foreground/70 hover:text-foreground transition-colors font-sans">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-muted animate-pulse rounded-sm" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-base font-medium uppercase tracking-[0.1em] text-foreground hover:text-primary transition-colors font-sans" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <div className="pt-6 border-t border-border">
        <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground uppercase text-sm tracking-[0.1em] h-14 rounded-none bg-background" asChild onClick={onClose}>
          <Link to="/#book">Book Now</Link>
        </Button>
      </div>
    </nav>;
}
export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="site-navigation" className={cn("sticky top-0 z-50 w-full transition-all duration-500 ease-in-out border-b bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-sm" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex h-20 items-center justify-between">
          {}
          <div className="flex-shrink-0 p-0">
            <Link to="/HomePage" className="block leading-none">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614369/img/xo_boudoir_luxury_script_logo.png" alt="XO Boudoir" className="h-7 object-contain" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex items-center gap-10">
            <MenuProvider menu_id="2" className="hidden md:block">
              <DesktopMenuConsumer />
            </MenuProvider>

            {}
            <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground uppercase text-[13px] tracking-[0.1em] px-10 h-12 font-medium transition-all duration-300 rounded-none bg-background" asChild>
              <Link to="/#book">Book Now</Link>
            </Button>
          </div>

          {}
          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-transparent" aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      <div className={cn("fixed inset-x-0 top-20 bottom-0 z-40 bg-background border-t border-border md:hidden transition-all duration-300 ease-in-out overflow-y-auto", isMobileMenuOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-4 pointer-events-none")}>
        <MenuProvider menu_id="2">
          <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </header>;
}